import React, { useState, useRef, useEffect } from 'react';
import { Select, Spin } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { t } from '../i18n';
import { useFilterLayoutStyles } from '../utils/languageUtils';


interface OptionType {
    label: string;
    value: string | number;
    subLabel?: string;
}

interface SearchSelectProps {
    label: string;
    value?: string | number;
    onChange?: (value: string | number | undefined) => void;
    onSearch?: (searchValue: string, callback: (data: OptionType[]) => void) => void;
    placeholder?: string;
    className?: string;
    spanClassName?: string;
    disabled?: boolean;
    debounceTime?: number;
    defaultActiveFirstOption?: boolean;
    notFoundContent?: React.ReactNode;
    required?: boolean;
    forceHorizontal?: boolean; // 强制水平布局，如果为 true 则无论语言都使用水平布局
}

/**
 * 支持远程搜索的下拉选择器
 * @description 支持通过 onSearch 回调进行远程搜索，自动防抖处理
 */
const SearchSelect: React.FC<SearchSelectProps> = ({
    label,
    value,
    onChange,
    onSearch,
    placeholder,
    className,
    spanClassName,
    disabled = false,
    debounceTime = 300,
    defaultActiveFirstOption = false,
    notFoundContent = null,
    required = false,
    forceHorizontal,
}) => {
    // 使用公共 Hook 获取布局样式
    const { containerClassName, labelClassName, inputContainerClassName } = useFilterLayoutStyles(spanClassName, className, forceHorizontal);
    const [data, setData] = useState<OptionType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const currentValueRef = useRef<string>('');

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleSearch = (searchValue: string) => {
        if (!onSearch) {
            return;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        currentValueRef.current = searchValue;

        if (searchValue) {
            setHasSearched(true);
            timeoutRef.current = setTimeout(() => {
                setData([]);
                setLoading(true);
                onSearch(searchValue, (options: OptionType[]) => {
                    if (currentValueRef.current === searchValue) {
                        setData(options);
                        setLoading(false);
                    }
                });
            }, debounceTime);
        } else {
            setData([]);
            setLoading(false);
            setHasSearched(false);
        }
    };

    const handleChange = (selectedValue: string | number | undefined) => {
        if (onChange) {
            onChange(selectedValue);
        }
    };

    const handleClear = () => {
        setData([]);
        setLoading(false);
        setHasSearched(false);
        currentValueRef.current = '';
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (onChange) {
            onChange(undefined);
        }
    };

    return (
        <div className={containerClassName}>
            <span className={labelClassName}>
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </span>
            <div className={inputContainerClassName}>
                <Select
                    showSearch
                    value={value === '' || value === null || value === undefined ? undefined : value}
                    placeholder={placeholder || t('components.input.all')}
                    style={{ width: '100%' }}
                    defaultActiveFirstOption={defaultActiveFirstOption}
                    suffixIcon={<DownOutlined />}
                    filterOption={false}
                    onSearch={handleSearch}
                    onChange={handleChange}
                    onClear={handleClear}
                    notFoundContent={loading ? <Spin size="small" /> : (hasSearched && data.length === 0 ? (notFoundContent || 'No data') : null)}
                    loading={loading}
                    disabled={disabled}
                    allowClear
                    autoFocus={false}
                    size="middle"
                    options={data.map(item => ({ value: item.value, label: item.label, subLabel: item.subLabel }))}
                    optionRender={(option) => (
                        <div className="flex justify-between w-full gap-2">
                            <span>{option.data.label}</span>
                            {option.data.subLabel && (
                                <span className="text-gray-400 shrink-0 text-sm">{option.data.subLabel}</span>
                            )}
                        </div>
                    )}
                />
            </div>
        </div>
    );
};

export default SearchSelect;

